import {Controller} from "stimulus";

/**
 * ListFilterController filters rows in a list by matching a typed query
 * against a data attribute on each row target, with optional select-based
 * filtering on a second attribute.
 *
 * Usage (text-only, e.g. column filter on data dictionary show page):
 *   - Set `data-controller="lighthouse--list-filter"` and
 *     `data-lighthouse--list-filter-attribute-value="columnName"` on a
 *     wrapper element.
 *   - Set `data-lighthouse--list-filter-target="query"` and
 *     `data-action="input->lighthouse--list-filter#filter"` on the search
 *     input.
 *   - Set `data-lighthouse--list-filter-target="item"` and
 *     `data-column-name="<name>"` on each filterable row element.
 *
 * Usage (text + select, e.g. table filter on data dictionary index page):
 *   - As above, with `data-lighthouse--list-filter-attribute-value="tableName"`.
 *   - Set `data-lighthouse--list-filter-target="select"` and
 *     `data-action="change->lighthouse--list-filter#filter"` on a select
 *     element.
 *   - Set `data-lighthouse--list-filter-select-attribute-value="visibility"`
 *     on the controller element.
 *   - Add `data-visibility="<value>"` on each item target.
 */
export default class ListFilterController extends Controller {
  static targets = ["item", "query", "select"];
  static values = {
    attribute: String,
    selectAttribute: {type: String, default: ""},
  };

  declare readonly itemTargets: HTMLElement[];
  declare readonly queryTarget: HTMLInputElement;
  declare readonly hasSelectTarget: boolean;
  declare readonly selectTarget: HTMLSelectElement;
  declare readonly attributeValue: string;
  declare readonly selectAttributeValue: string;

  filter() {
    const query = this.queryTarget.value.trim().toLowerCase();
    const selectValue = this.hasSelectTarget ? this.selectTarget.value : "";

    this.itemTargets.forEach((item) => {
      const matchesQuery =
        !query ||
        (item.dataset[this.attributeValue] ?? "").toLowerCase().includes(query);
      const matchesSelect =
        !selectValue || this.matchesSelect(item, selectValue);

      if (matchesQuery && matchesSelect) {
        item.classList.remove("hidden");
      } else {
        item.classList.add("hidden");
      }
    });
  }

  private matchesSelect(item: HTMLElement, filterValue: string): boolean {
    const itemValue = item.dataset[this.selectAttributeValue] ?? "";
    if (filterValue === "not_set") return itemValue === "";
    return itemValue === filterValue;
  }
}
